// Help content API service
import { apiService } from './apiService';
import {
  HelpContent,
  FAQItem,
  TroubleshootingGuide,
  HelpSearchRequest,
  HelpSearchResult
} from '../types/help';

class HelpService {
  private baseUrl = '/api/v1/help';

  // Help Content Management
  async getHelpContent(component: string, tenantId: string): Promise<HelpContent[]> {
    try {
      const response = await apiService.get<HelpContent[]>(`${this.baseUrl}/content?component=${encodeURIComponent(component)}`, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching help content:', error);
      throw error;
    }
  }

  async getFAQ(tenantId: string, category?: string): Promise<FAQItem[]> {
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      
      const response = await apiService.get<FAQItem[]>(`${this.baseUrl}/faq?${params.toString()}`, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching FAQ:', error);
      throw error;
    }
  }

  async getTroubleshootingGuides(tenantId: string, category?: string): Promise<TroubleshootingGuide[]> {
    try {
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      
      const response = await apiService.get<TroubleshootingGuide[]>(`${this.baseUrl}/troubleshooting?${params.toString()}`, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching troubleshooting guides:', error);
      throw error;
    }
  }

  async searchHelpContent(searchRequest: HelpSearchRequest, tenantId: string): Promise<HelpSearchResult[]> {
    try {
      const response = await apiService.post<HelpSearchResult[]>(`${this.baseUrl}/search`, searchRequest, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching help content:', error);
      throw error;
    }
  }

  async getHelpCategories(tenantId: string): Promise<string[]> {
    try {
      const response = await apiService.get<string[]>(`${this.baseUrl}/categories`, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching help categories:', error);
      throw error;
    }
  }

  // Context-sensitive help
  async getTooltipContent(component: string, triggerEvent: string, tenantId: string): Promise<HelpContent | null> {
    try {
      const response = await apiService.get<HelpContent>(`${this.baseUrl}/tooltip?component=${encodeURIComponent(component)}&trigger_event=${encodeURIComponent(triggerEvent)}`, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching tooltip content:', error);
      return null;
    }
  }

  async getOverlayContent(component: string, tenantId: string): Promise<HelpContent | null> {
    try {
      const response = await apiService.get<HelpContent>(`${this.baseUrl}/overlay?component=${encodeURIComponent(component)}`, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching overlay content:', error);
      return null;
    }
  }

  // Help content analytics
  async trackHelpUsage(
    contentId: string,
    action: 'view' | 'click' | 'search',
    tenantId: string
  ): Promise<void> {
    try {
      await apiService.post(`${this.baseUrl}/analytics`, {
        content_id: contentId,
        action,
        timestamp: new Date().toISOString()
      }, {
        headers: { 'X-Tenant-ID': tenantId }
      });
    } catch (error) {
      console.error('Error tracking help usage:', error);
      // Don't throw error for analytics tracking
    }
  }
}

export const helpService = new HelpService();
